import type { Ref } from 'vue';

import type {
  FreightDelivery,
  DeliveryMethodWarehouse,
  FreightDeliveryMethod,
  FreightOrderLine,
  PackagingLine,
  PackagingResponse,
  PackagingOrderLine,
} from '@/data/api/freight/freight.d';
import type { Article } from '@/types/Article';
import type { OrderProductlistRow, ProductlistRow } from '@/types/Productlist';

import _ from 'lodash';
import i18n from '@/languages/i18n';

import { formatWeight, convertUnit, roundUnit } from '@/data/api/products/helpers';
import { getArticleTitle } from '@/helpers';
import { PriceManager, PriceType } from './pricemanager';
import { PriceCollection } from '@/products';
import ArticleHelper from './article';

export function splitOrderLinesByFreight(rows: any[], deliveries: FreightDelivery[]): any[][] {
  const list: any[][] = [];
  // Each row needs to be placed in a delivery. Iterate all rows and identify which delivery they belong to.
  for (const row of rows) {
    let deliveryRow: FreightOrderLine | undefined,
      deliveryIndex = -1;
    for (deliveryIndex = 0; deliveryIndex < deliveries.length; deliveryIndex++) {
      deliveryRow = deliveries[deliveryIndex].orderLines.find((orderLine) => orderLine.lineNumber === row.lineNumber);

      if (deliveryRow) break;
    }

    if (!deliveryRow) {
      // TODO: This might be real bad if this can actually happen. Might need to act on it
      console.error("Freight response didn't include order line", row);
      console.error('Skipping order line');
      continue;
    }

    if (!list[deliveryIndex]) list[deliveryIndex] = [];
    list[deliveryIndex].push({
      ...row,
    });
  }

  return list;
}

// Returns a clone of the row if it exists in step1Rows, otherwise undefined
export function matchOrderRow(
  step1Rows: any[],
  orderLine: FreightOrderLine | PackagingOrderLine
): OrderProductlistRow | undefined {
  const lineKey = new LineKey(orderLine);
  let existingRow = _.cloneDeep(
    step1Rows.find((row) => {
      const rowKey = new LineKey(row);
      return rowKey.isEqual(lineKey);
    })
  );

  // Update itemNumber etc. if we find a match
  if (existingRow) {
    existingRow.itemNumber = orderLine.itemNumber;
    existingRow.quantity = orderLine.quantity;
    existingRow.unit = orderLine.unit;
  }

  return existingRow;
}

export function getPrice(
  row: FreightOrderLine | ProductlistRow,
  type: Exclude<PriceType, PriceType.Line | PriceType.M2>
) {
  let prices = [] as any[];
  const manager = row.price_manager as PriceManager;
  const isProductlistRow = 'needsQuote' in row && 'isBlocked' in row;

  const showM2Price = type == PriceType.Net && (!('unitDetails' in row) || !row.unitDetails);

  // Total line price
  prices.push({
    needsQuote: isProductlistRow ? row.needsQuote : false,
    isBlocked: isProductlistRow ? row.isBlocked : false,
    value: {
      price: manager?.getPriceTotal(type == PriceType.Net ? PriceType.Line : type), // A bit weird, but we actually want the line price here for Net
    },
  });

  // Unit Price (e.g 'alternate' price)
  prices.push({
    needsQuote: isProductlistRow ? row.needsQuote : false,
    isBlocked: isProductlistRow ? row.isBlocked : false,
    value: {
      price: manager?.getPrice(type),
      unit: manager?.getBaseUnit(type),
      parenthesis: true,
    },
  });

  if (showM2Price && manager?.exists(PriceType.M2)) {
    prices.push({
      needsQuote: isProductlistRow ? row.needsQuote : false,
      isBlocked: isProductlistRow ? row.isBlocked : false,
      value: {
        price: manager?.getPrice(PriceType.M2),
        // class: ['is-size-8'],
        unit: i18n.global.t('unit.short.m2'),
        parenthesis: true,
      },
    });
  }

  return prices;
}

export function getAddonPrice(
  row: FreightOrderLine | ProductlistRow,
  type: Exclude<PriceType, PriceType.Line | PriceType.M2>
) {
  const manager = row.price_manager as PriceManager;
  let prices = [] as any[];
  let price = manager?.getPrice(type);
  const isProductlistRow = 'needsQuote' in row && 'isBlocked' in row;

  prices.push({
    needsQuote: isProductlistRow ? row.needsQuote : false,
    isBlocked: isProductlistRow ? row.isBlocked : false,
    value: {
      price: manager?.getPriceTotal(type == PriceType.Net ? PriceType.Line : type), // A bit weird, but we actually want the line price here for Net
    },
  });

  let unit = row.unit;
  if (row.unit == 'kg') {
    unit = 'ton';
    price = price.multiply(1000) ?? price;
    // netPrice = roundCurrency(price?.netPrice * 1000, 'ton', 'round');
    // salesPrice = roundCurrency(price?.salesPrice * 1000, 'ton', 'round');
  }

  prices.push({
    needsQuote: isProductlistRow ? row.needsQuote : false,
    isBlocked: isProductlistRow ? row.isBlocked : false,
    value: {
      price: price,
      unit: unit,
      parenthesis: true,
    },
  });

  return prices;
}

export function getDiscounts(row: FreightOrderLine | ProductlistRow) {
  return _.range(1, 7)
    .map((index) => {
      const discount = row.price_manager?.getDiscount(PriceType.Line, index);
      if (!discount) return {};
      return {
        index: index,
        discountFactor: discount.discount,
        discountText: discount.text,
        statusDiscount: discount.status,
      };
    })
    .filter((disc) => parseFloat(disc.discountFactor) > 0)
    .map((disc) => ({
      value: `${i18n.global.t('price.discount')} ${disc.index}: ${disc.discountText}<br>${disc.discountFactor} %`,
      style: 'padding: 0.25rem 0',
    }));
}

export interface RowHandlers {
  cache: Ref<Record<string, Article>>;
  mobile: Ref<boolean>;
  region: Ref<string | undefined>;
  customerNumber: Ref<string | undefined>;
  isM3Order: boolean;
  address?: Ref<string | undefined>;
  warehouse?: Ref<string | undefined>; // Fallback if no warehouse is found on row
  packaging: (row: FreightOrderLine | PackagingLine, art: Article, warehouse: string) => Record<string, any>;
  freight: (row: FreightOrderLine, art: Article, warehouse: string) => Record<string, any>;
  standard: (row: OrderProductlistRow, art: Article, warehouse: string) => Record<string, any>;
}

export function newFreightRowHandlers({
  cache,
  mobile,
  region,
  customerNumber,
  warehouse = undefined,
  isM3Order = false,
}: {
  cache: Ref<Record<string, Article>>;
  mobile: Ref<boolean>;
  region: Ref<string | undefined>;
  customerNumber: Ref<string | undefined>;
  isM3Order: boolean;
  warehouse?: Ref<string | undefined>;
}): RowHandlers {
  return {
    cache,
    mobile,
    region,
    customerNumber,
    warehouse,
    isM3Order,
    packaging: function (row: FreightOrderLine | PackagingLine, art: Article, _warehouse: string) {
      const weight = art.chargeableWeight * Math.ceil(row.quantity);
      const id = row.lineNumber;

      return {
        class: 'dynamic',
        id: id,
        _meta: { addon: true, addon_type: 'pallet', readonly: true },
        line: 'lineNumber' in row ? { value: row.lineNumber } : null,
        name_and_warehouse_status: [
          {
            component: 'name',
            name: 'name',
            label: getArticleTitle(art),
            article: art,
          },
        ],
        quantity_unit_pieces: {
          component: 'string',
          article: null,
          value: `${row.quantity} ${convertUnit(row.unit || 'st', {
            type: !['p', 'p2'].includes(row.unit) ? 'short' : 'long',
            lowercase: false,
          })}`,
        },
        weight: {
          value: weight ? formatWeight(weight) + ` ${i18n.global.t('unit.short.kg')}` : '',
        },
        gross_price: getAddonPrice(row, PriceType.Sales),
        discount: getDiscounts(row),
        net_price: getAddonPrice(row, PriceType.Net),
      };
    },
    freight: function (row: FreightOrderLine, art: Article, warehouse: string) {
      return {
        class: 'dynamic',
        id: row.lineNumber,
        line: { value: row.lineNumber },
        _meta: { missingFromStep1: true },
        // image: { article: art },
        name_and_warehouse_status: [
          {
            _meta: undefined,
            component: 'name',
            name: 'name',
            label: getArticleTitle(art),
            article: art,
          },
          {
            _meta: undefined,
            component: 'warehouse_status',
            name: 'warehouse_status',
            selectable: false,
            removable: !this.isM3Order,
            region: this.region.value,
            article: art,
            warehouse: warehouse,
            customer_number: this.customerNumber.value,
            amount: row.quantity,
            unit: row.unit,
            type: 'checkout',
          },
        ],
        quantity_unit_pieces: {
          component: 'string',
          article: art,
          value: `${row.quantity} ${convertUnit(row.unit || 'st', {
            type: !['p', 'p2'].includes(row.unit) ? 'short' : 'long',
            lowercase: false,
          })}`,
        },
        weight: {
          value: art.chargeableWeight ? formatWeight(art.chargeableWeight) + ` ${i18n.global.t('unit.short.kg')}` : '',
        },
        gross_price: getAddonPrice(row, PriceType.Sales),
        discount: getDiscounts(row),
        net_price: getAddonPrice(row, PriceType.Net),
      };
    },
    standard: function (row: OrderProductlistRow, art: Article, warehouse: string) {
      // Try and keep the ID the same to limit re-drawing, and limit what needs to be animated
      // (If we start handling the orderlinenumber, we can remove this and just just orderlinenumber)
      const getQuantityAndUnitPieces = () => {
        const showAlternateUnit = !!row.unitDetails?.alternateUnit;
        const showSalesUnit =
          row.unitDetails?.salesUnit &&
          row.unitDetails.salesUnit !== row.unitDetails.baseUnit &&
          row.unitDetails.salesUnit !== row.unitDetails.alternateUnit;
        const showBaseUnit = !showAlternateUnit || row.unitDetails?.alternateUnit !== row.unitDetails?.baseUnit;

        const data = [] as any[];

        // Append the alternate unit if it exists
        if (row.unitDetails && showAlternateUnit) {
          data.push({
            component: 'string',
            article: art,
            value: `${roundUnit(row.unitDetails.alternateQuantity, 2)} ${convertUnit(
              row.unitDetails.alternateUnit || 'st',
              {
                type: !['p', 'p2'].includes(row.unitDetails.alternateUnit) ? 'short' : 'long',
                lowercase: false,
              }
            )}`,
          });
        }

        // Append the sales unit if it exists
        if (row.unitDetails && showSalesUnit) {
          data.push({
            component: 'string',
            article: art,
            value: `${roundUnit(row.unitDetails.salesQuantity, 2)} ${convertUnit(row.unitDetails.salesUnit || 'st', {
              type: !['p', 'p2'].includes(row.unitDetails.salesUnit) ? 'short' : 'long',
              lowercase: false,
            })}`,
          });
        }

        // Append the base unit
        if (showBaseUnit) {
          data.push({
            component: 'string',
            article: art,
            value: `${row.quantity} ${convertUnit(row.unit || 'st', {
              type: !['p', 'p2'].includes(row.unit) ? 'short' : 'long',
              lowercase: false,
            })}`,
          });
        }

        // Wrap each line in parathese, except the first one
        for (let i = 1; i < data.length; i++) {
          data[i].value = `(${data[i].value})`;
        }

        return data;
      };

      return {
        id: row.id ?? row.lineNumber,
        line: {
          value: row.lineNumber,
        },
        image: { article: art },
        name_and_warehouse_status: [
          {
            _meta: row._meta,
            component: 'name',
            name: 'name',
            label: getArticleTitle(art),
            article: art,
            batchnr: row.batchnr,
          },
          {
            _meta: row._meta,
            component: 'warehouse_status',
            name: 'warehouse_status',
            selectable: false,
            removable: !this.isM3Order,
            region: this.region.value,
            article: art,
            warehouse: warehouse,
            customer_number: this.customerNumber.value,
            amount: row.amount,
            unit: row.unit,
            batchnr: row.batchnr,
            type: 'checkout',
          },
        ],
        quantity_unit_pieces: getQuantityAndUnitPieces(),
        weight: {
          value: row.weight ? formatWeight(row.weight) + ` ${i18n.global.t('unit.short.kg')}` : '',
        },
        gross_price: getPrice(row, PriceType.Sales),
        discount: getDiscounts(row),
        net_price: getPrice(row, PriceType.Net),
        _is_underlying: row.isUnderlying === true,
      };
    },
  };
}

// export function buildOrderData(
//   delivery?: Delivery,
//   freight?: FreightFreightLine,
//   step1Rows: any[] = []
// ): any[] {
// }
interface deliveryMetadata {
  address: string | undefined;
  customerNumber: string | undefined;
  region: string | undefined;
  warehouse?: string;
}

export interface FreightDeliveryOrderLine {
  id?: string;
  lineNumber: string;
  lineNumberPosition: string;
  itemNumber: string;
  unit: string;
  quantity: number;
  transactionReason: string;

  article?: Article;
  warehouse?: string;
  weight?: number;

  prices?: PriceCollection;
  price_manager?: PriceManager;
}

interface LinesWrapper {
  warehouse: string;
  lines: FreightDeliveryOrderLine[];
}

const calcAmount = (line): any => {
  return roundUnit(
    line.quantity * (line.article?.unit?.[line.unit || 'st'] || 0) || line.quantity,
    line.article.numberOfDecimalPlaces,
    false
  ) as number;
};

async function buildDeliveryRows(
  meta: deliveryMetadata,
  linesWrappers: LinesWrapper[],
  step1Rows: OrderProductlistRow[] = []
): Promise<FreightDeliveryOrderLine[]> {
  const requiredArticles = new Set<string>();

  // Collect required articles
  for (const wrapper of linesWrappers) {
    for (const line of wrapper.lines) {
      if (line.itemNumber) requiredArticles.add(line.itemNumber);
    }
  }

  // Fetch articles that are not already in the cache
  const articles = Object.values(
    (await ArticleHelper.getAll({ artnrs: Array.from(requiredArticles) })) as Record<string, Article>
  ).reduce(
    (acc, art) => {
      acc[art.ArtNr] = art;
      return acc;
    },
    {} as Record<string, Article>
  );

  return linesWrappers.flatMap((wrapper) => {
    return wrapper.lines.map((line) => {
      const existingRow = matchOrderRow(step1Rows ?? [], line as FreightOrderLine);
      const artnr = line.itemNumber;
      const art = articles[artnr];

      if (!art) {
        console.error('Missing article', artnr);
        return {};
      }

      line.id = existingRow?.id;
      line.article = art;
      line.warehouse = wrapper.warehouse;
      line.weight = existingRow?.weight ?? 0;

      if (!existingRow && (art.userDefinedField1Item === 'FRAKT' || art.userDefinedField1Item === 'PALL')) {
        line.price_manager = new PriceManager(
          {
            artnr: line.itemNumber,
            customer: meta.customerNumber ?? '',
            address: meta.address ?? '',
            warehouse: wrapper.warehouse,
            region: meta.region ?? '',
            quantity: calcAmount(line),
          },
          {
            net: line.prices?.price_net,
            sales: line.prices?.price_sales,
            m2: line.prices?.price_m2,
            line: line.prices?.price_line,
          }
        );
      } else if (existingRow) {
        line.price_manager = existingRow.override_prices || existingRow.price_manager;

        if (!line.price_manager) {
          console.error('Missing price manager', line);
        }
      }

      return line;
    });
  }) as FreightDeliveryOrderLine[];
}

async function getFreightDeliveryRows(
  meta: deliveryMetadata,
  deliveryWarehouses?: DeliveryMethodWarehouse[],
  step1Rows: OrderProductlistRow[] = []
): Promise<FreightDeliveryOrderLine[]> {
  const linesWrapper = [] as {
    warehouse: string;
    lines: FreightDeliveryOrderLine[];
  }[];
  // Iterate all deliveries and their warehouses to get the order lines and articles
  for (const deliveryWarehouse of deliveryWarehouses ?? []) {
    for (const delivery of deliveryWarehouse.delivery ?? []) {
      linesWrapper.push({
        warehouse: deliveryWarehouse.warehouse,
        lines: delivery.orderLines.map((line) => {
          return {
            lineNumber: line.lineNumber,
            lineNumberPosition: line.lineNumberPosition,
            itemNumber: line.itemNumber,
            unit: line.unit,
            quantity: line.quantity,
            transactionReason: line.transactionReason,
            prices: line.prices,
            price_manager: line.price_manager,
          };
        }),
      });
    }
  }

  return await buildDeliveryRows(meta, linesWrapper, step1Rows);
}

async function getPackagingLineRows(
  meta: deliveryMetadata,
  packaging?: PackagingResponse,
  step1Rows: OrderProductlistRow[] = []
): Promise<any[]> {
  const linesWrapper = [] as {
    warehouse: string;
    lines: FreightDeliveryOrderLine[];
  }[];
  const lines = [] as any[];
  for (const line of packaging?.orderPackagingResponseOrderLines ?? []) {
    const existingRow = matchOrderRow(step1Rows ?? [], line);
    const artnr = existingRow?.itemNumber ?? line.itemNumber;
    lines.push({
      lineNumber: line.lineNumber,
      lineNumberPosition: line.lineNumberPosition,
      itemNumber: artnr,
      unit: line.unit,
      quantity: line.quantity,
      prices: line.prices,
      price_manager: line.price_manager,
    });
  }
  for (const line of packaging?.orderPackagingResponsePackageLines ?? []) {
    lines.push({
      lineNumber: line.lineNumber,
      lineNumberPosition: line.lineNumberPosition,
      itemNumber: line.itemNumber,
      unit: line.unit,
      quantity: line.quantity,
      prices: line.prices,
      price_manager: line.price_manager,
    });
  }
  linesWrapper.push({
    warehouse: meta.warehouse ?? '',
    lines: lines,
  });

  return await buildDeliveryRows(meta, linesWrapper, step1Rows);
}

export async function getRows(
  meta: deliveryMetadata,
  input?: FreightDeliveryMethod | PackagingResponse,
  step1Rows: OrderProductlistRow[] = []
): Promise<FreightDeliveryOrderLine[]> {
  if (!input) return [];

  if ('warehouses' in input) {
    return getFreightDeliveryRows(
      meta as deliveryMetadata,
      input?.warehouses,
      step1Rows.filter((r) => r.isUnderlying !== true)
    );
  } else if ('orderPackagingResponseOrderLines' in input) {
    return getPackagingLineRows(
      meta as deliveryMetadata,
      input,
      step1Rows.filter((r) => r.isUnderlying !== true)
    );
  }

  return [];
}

export async function formatPackagingListRows(
  h: RowHandlers,
  packaging?: PackagingResponse,
  step1Rows: any[] = []
): Promise<ProductlistRow[]> {
  const lines = await getPackagingLineRows(
    {
      address: h.address?.value,
      customerNumber: h.customerNumber.value,
      region: h.region.value,
      warehouse: h.warehouse?.value ?? '',
    },
    packaging,
    step1Rows.filter((r) => r.isUnderlying !== true)
  );

  let totalWeight = 0;
  const data: any[] = lines
    .map((line) => {
      // Handle articles based on their 'Type' field
      const warehouse = line.warehouse ?? h.warehouse ?? '';
      switch (line.article?.userDefinedField1Item) {
        case 'PALL':
          totalWeight += line.article.chargeableWeight * Math.ceil(line.quantity);
          return h.packaging(line, line.article, h.warehouse?.value ?? '');
        case 'FRAKT':
          // Should never happen, but log it if it does
          console.warn('FRAKT line in packaging, skipping as this should not happen', line);
          return {};
        default:
          const existingRow = matchOrderRow(step1Rows ?? [], line as FreightOrderLine);
          // Requires a row from step 1 to be present
          if (!existingRow) {
            console.error('Missing row', line);
            return {};
          }

          if (!line.lineNumber || !line.article) {
            console.error('Missing line number or article', line);
            return {};
          }

          totalWeight += +(existingRow.weight || 0);

          // Re-calculate the alternate and salesUnit amounts as quantity might have changed
          if (existingRow.unitDetails && existingRow.unitDetails.baseQuantity != line.quantity) {
            const baseQuantity = existingRow.unitDetails.baseQuantity;

            if (existingRow.unitDetails.salesUnit) {
              const salesQuantity = existingRow.unitDetails.salesQuantity;
              existingRow.unitDetails.salesQuantity = line.quantity / (baseQuantity / salesQuantity);
            }
            if (existingRow.unitDetails.alternateUnit) {
              const alternateQuantity = existingRow.unitDetails.alternateQuantity;
              existingRow.unitDetails.alternateQuantity = line.quantity / (baseQuantity / alternateQuantity);
            }
          }

          // Update the existing row with the new values
          existingRow.quantity = line.quantity;
          existingRow.unit = line.unit;
          existingRow.lineNumber = line.lineNumber;

          return h.standard(existingRow, line.article, warehouse);
      }
    })
    .filter((l) => l._is_underlying !== true);

  // Append a row with total weight
  if (!h.mobile.value) {
    const total = totalWeight?.toFixed(2);

    const formattedWeight = !isNaN(totalWeight) ? formatWeight(total) : '...';

    data.push({
      class: 'dynamic',
      weight: {
        class: 'has-text-weight-bold is-size-7 nowrap',
        value: formattedWeight + ` ${i18n.global.t('unit.short.kg')}`,
      },
    });
  }

  return data;
}

export async function formatFreightListRows(
  h: RowHandlers,
  delivery?: FreightDelivery,
  deliveryWarehouse?: DeliveryMethodWarehouse,
  step1Rows: any[] = []
): Promise<ProductlistRow[]> {
  const lines = await getFreightDeliveryRows(
    {
      address: h.address?.value,
      customerNumber: h.customerNumber.value,
      region: h.region.value,
      warehouse: deliveryWarehouse?.warehouse ?? '',
    },
    [
      {
        warehouse: deliveryWarehouse?.warehouse ?? '',
        delivery: delivery ? [delivery] : [],
      },
    ],
    step1Rows.filter((r) => r.isUnderlying !== true)
  );

  let totalWeight = 0;
  const data: any[] = lines
    .map((line) => {
      // Handle articles based on their 'Type' field
      const warehouse = line.warehouse ?? deliveryWarehouse?.warehouse ?? '';
      switch (line.article?.userDefinedField1Item) {
        case 'PALL':
          totalWeight += line.article.chargeableWeight * Math.ceil(line.quantity);
          return h.packaging(line as FreightOrderLine, line.article, warehouse);
        case 'FRAKT':
          return h.freight(line as FreightOrderLine, line.article, warehouse);
        default:
          const existingRow = matchOrderRow(step1Rows ?? [], line as FreightOrderLine);
          // Requires a row from step 1 to be present
          if (!existingRow) {
            console.error('Missing row', line);
            return {};
          }

          if (!line.lineNumber || !line.article) {
            console.error('Missing line number or article', line);
            return {};
          }

          totalWeight += +(existingRow.weight || 0);

          // Re-calculate the alternate and salesUnit amounts as quantity might have changed
          if (existingRow.unitDetails && existingRow.unitDetails.baseQuantity != line.quantity) {
            const baseQuantity = existingRow.unitDetails.baseQuantity;

            if (existingRow.unitDetails.salesUnit) {
              const salesQuantity = existingRow.unitDetails.salesQuantity;
              existingRow.unitDetails.salesQuantity = line.quantity / (baseQuantity / salesQuantity);
            }
            if (existingRow.unitDetails.alternateUnit) {
              const alternateQuantity = existingRow.unitDetails.alternateQuantity;
              existingRow.unitDetails.alternateQuantity = line.quantity / (baseQuantity / alternateQuantity);
            }
          }

          // Update the existing row with the new values
          existingRow.quantity = line.quantity;
          existingRow.unit = line.unit;
          existingRow.lineNumber = line.lineNumber;

          return h.standard(existingRow, line.article, warehouse);
      }
    })
    .filter((l) => l._is_underlying !== true);

  // Append a row with total weight
  if (!h.mobile.value) {
    const total = totalWeight?.toFixed(2);

    const formattedWeight = !isNaN(totalWeight) ? formatWeight(total) : '...';

    data.push({
      class: 'dynamic',
      weight: {
        class: 'has-text-weight-bold is-size-7 nowrap',
        value: formattedWeight + ` ${i18n.global.t('unit.short.kg')}`,
      },
    });
  }

  return data;
}

export class LineKey {
  lineNumber: string;
  lineNumberPosition: string;
  constructor(line: { lineNumber?: string; lineNumberPosition?: string }) {
    this.lineNumber = line.lineNumber ?? '0';
    this.lineNumberPosition = line.lineNumberPosition ?? '0';
  }

  isEqual(key: LineKey): boolean {
    return this.lineNumber === key.lineNumber && this.lineNumberPosition === key.lineNumberPosition;
  }

  toString(): string {
    return `${this.lineNumber}-${this.lineNumberPosition}`;
  }

  static fromString(key: string): LineKey {
    const [lineNumber, lineNumberPosition] = key.split('-');
    return new LineKey({ lineNumber, lineNumberPosition });
  }
}
